import "./style.css";
import { initCrypto } from "./crypto";
import Connection, { type Status, type CursorImage, type RemoteEntry } from "./connection";
import { VideoRenderer } from "./video";
import {
  buildConfig,
  loadForm,
  saveForm,
  remoteIdFromHash,
  type ConfigForm,
} from "./config";
import { initHandoff, type HandoffPayload } from "./handoff";
import {
  fetchDevices,
  isPortalEnabled,
  portalLoginUrl,
  PortalAuthError,
  type PortalDevice,
} from "./portal";
import { hbb } from "./proto/messages.js";

const app = document.querySelector<HTMLDivElement>("#app")!;
app.innerHTML = `
  <div id="bar">
    <button id="devicesBtn" title="Devices" hidden>Devices</button>
    <input id="peerId" placeholder="Remote ID" autocomplete="off" />
    <button id="connect">Connect</button>
    <button id="disconnect" disabled>Disconnect</button>
    <button id="mute" title="Mute remote audio" disabled></button>
    <button id="filesBtn" title="File transfer" disabled>Files</button>
    <select id="quality" title="Image quality" disabled>
      <option value="balanced">Balanced</option>
      <option value="low">Low (fast)</option>
      <option value="best">Best</option>
    </select>
    <select id="fps" title="Max FPS" disabled>
      <option value="30">30 fps</option>
      <option value="60">60 fps</option>
      <option value="20">20 fps</option>
      <option value="15">15 fps</option>
      <option value="10">10 fps</option>
      <option value="5">5 fps</option>
    </select>
    <select id="display" title="Monitor" hidden></select>
    <button id="settingsToggle" title="Server settings">⚙ Server</button>
    <span id="status">idle</span>
  </div>
  <div id="settings">
    <input id="idServer" placeholder="ID / Rendezvous server (host or host:port)" autocomplete="off" />
    <input id="relayServer" placeholder="Relay server (optional)" autocomplete="off" />
    <input id="key" placeholder="Key (base64, optional but needed for encryption)" autocomplete="off" />
    <label>TLS
      <select id="schema">
        <option value="wss://">wss:// (secure)</option>
        <option value="ws://">ws:// (LAN/testing)</option>
      </select>
    </label>
  </div>
  <div id="stage">
    <div id="portal" hidden>
      <div class="phead">
        <input id="psearch" placeholder="Search devices…" autocomplete="off" />
        <button id="prefresh" title="Refresh">⟳</button>
        <button id="pclose" title="Close">✕</button>
      </div>
      <div id="pnote"></div>
      <ul id="plist"></ul>
    </div>
    <canvas id="screen" tabindex="0"></canvas>
    <div id="hostcursor" hidden></div>
    <div id="overlay"><div id="overlayText">Set your server (⚙), enter a Remote ID, then Connect</div></div>
    <div id="files" hidden>
      <div class="fhead">
        <button id="fup" title="Up">⬆</button>
        <span id="fpath">/</span>
        <button id="frefresh" title="Refresh">⟳</button>
        <label class="fuploadlbl">⬆ Upload<input id="fupload" type="file" multiple hidden /></label>
        <button id="fclose" title="Close">✕</button>
      </div>
      <div id="fnote"></div>
      <ul id="flist"></ul>
    </div>
  </div>
  <div id="pwmodal" hidden>
    <div class="card">
      <div id="pwtitle">Password required</div>
      <input id="pw" type="password" placeholder="Password" />
      <div class="row"><button id="pwok">OK</button><button id="pwcancel">Cancel</button></div>
    </div>
  </div>
`;

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const peerId = $<HTMLInputElement>("peerId");
const connectBtn = $<HTMLButtonElement>("connect");
const disconnectBtn = $<HTMLButtonElement>("disconnect");
const muteBtn = $<HTMLButtonElement>("mute");
const filesBtn = $<HTMLButtonElement>("filesBtn");
const devicesBtn = $<HTMLButtonElement>("devicesBtn");
const portalPanel = $<HTMLDivElement>("portal");
const psearch = $<HTMLInputElement>("psearch");
const pnote = $<HTMLDivElement>("pnote");
const plist = $<HTMLUListElement>("plist");
const qualitySelect = $<HTMLSelectElement>("quality");
const fpsSelect = $<HTMLSelectElement>("fps");
const displaySelect = $<HTMLSelectElement>("display");
const settingsToggle = $<HTMLButtonElement>("settingsToggle");
const filesPanel = $<HTMLDivElement>("files");
const fpath = $<HTMLSpanElement>("fpath");
const fnote = $<HTMLDivElement>("fnote");
const flist = $<HTMLUListElement>("flist");
const fuploadInput = $<HTMLInputElement>("fupload");
const settings = $<HTMLDivElement>("settings");
const idServerEl = $<HTMLInputElement>("idServer");
const relayServerEl = $<HTMLInputElement>("relayServer");
const keyEl = $<HTMLInputElement>("key");
const schemaEl = $<HTMLSelectElement>("schema");
const statusEl = $<HTMLSpanElement>("status");
const canvas = $<HTMLCanvasElement>("screen");
const hostCursorEl = $<HTMLDivElement>("hostcursor");
const overlay = $<HTMLDivElement>("overlay");
const overlayText = $<HTMLDivElement>("overlayText");
const pwmodal = $<HTMLDivElement>("pwmodal");
const pwInput = $<HTMLInputElement>("pw");
const pwTitle = $<HTMLDivElement>("pwtitle");

// ---- load saved settings ----------------------------------------------------
const saved = loadForm();
idServerEl.value = saved.server;
relayServerEl.value = saved.relay;
keyEl.value = saved.key;
schemaEl.value = saved.schema;
peerId.value = remoteIdFromHash();
// Show the settings panel until a server is set.
if (!saved.server) settings.classList.add("open");

settingsToggle.onclick = () => settings.classList.toggle("open");

function currentForm(): ConfigForm {
  return {
    server: idServerEl.value.trim(),
    relay: relayServerEl.value.trim(),
    key: keyEl.value.trim(),
    schema: schemaEl.value as ConfigForm["schema"],
  };
}

let conn: Connection | undefined;
let activeConfig: ReturnType<typeof buildConfig> | undefined;
let connectedId = "";
let lastPassword = ""; // plaintext, in-memory only, reused for the file session
let injectedPassword: string | null = null; // supplied by the dashboard hand-off; used once, never stored
let fileConn: Connection | undefined;
let fileReady = false;

function setStatus(s: Status, detail?: string) {
  statusEl.textContent = detail ? `${s} — ${detail}` : s;
  const active = !["idle", "closed", "error"].includes(s);
  connectBtn.disabled = active;
  disconnectBtn.disabled = !active;
  muteBtn.disabled = !active;
  filesBtn.disabled = !active;
  qualitySelect.disabled = !active;
  fpsSelect.disabled = !active;
}

// Quality / FPS — persisted and re-applied to the host once connected.
qualitySelect.value = localStorage.getItem("rd-quality") || "balanced";
fpsSelect.value = localStorage.getItem("rd-fps") || "30";
qualitySelect.onchange = () => {
  localStorage.setItem("rd-quality", qualitySelect.value);
  conn?.setImageQuality(qualitySelect.value as "low" | "balanced" | "best");
};
fpsSelect.onchange = () => {
  localStorage.setItem("rd-fps", fpsSelect.value);
  conn?.setFps(parseInt(fpsSelect.value, 10));
};
function applyQualityFps() {
  conn?.setImageQuality(qualitySelect.value as "low" | "balanced" | "best");
  conn?.setFps(parseInt(fpsSelect.value, 10));
}

// ---- file transfer (a dedicated FILE_TRANSFER connection) -------------------
const ROOT = "/"; // host lists drives (Windows) / root (*nix) for "/"
let currentPath = ROOT;

function joinPath(parent: string, name: string): string {
  if (parent === ROOT || parent === "") {
    return name.endsWith(":") ? name + "\\" : ROOT + name.replace(/^\/+/, ""); // Win drive vs *nix
  }
  const sep = parent.includes("\\") || /^[A-Za-z]:/.test(parent) ? "\\" : "/";
  return parent.replace(/[\\/]+$/, "") + sep + name;
}
function parentPath(p: string): string {
  if (!p || p === ROOT) return ROOT;
  if (/^[A-Za-z]:[\\/]?$/.test(p)) return ROOT; // drive root -> drive list
  const t = p.replace(/[\\/]+$/, "");
  const idx = Math.max(t.lastIndexOf("\\"), t.lastIndexOf("/"));
  if (idx < 0) return ROOT;
  if (idx === 0) return ROOT; // "/foo" -> "/"
  const par = t.slice(0, idx);
  return /^[A-Za-z]:$/.test(par) ? par + "\\" : par;
}

function fnoteMsg(text: string, color = "#aaa") {
  fnote.textContent = text;
  fnote.style.color = color;
}

const FOLDER_ICON =
  '<svg class="ficon" viewBox="0 0 24 24" width="16" height="16" fill="#e0b64d" aria-hidden="true"><path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z"/></svg>';
const FILE_ICON =
  '<svg class="ficon" viewBox="0 0 24 24" width="16" height="16" fill="#9fb0c0" aria-hidden="true"><path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6zm7 1.5L18.5 9H13V3.5z"/></svg>';

const MONITOR_ICON =
  '<svg class="ficon" viewBox="0 0 24 24" width="16" height="16" fill="#8fd0ff" aria-hidden="true"><path d="M3 4h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-7v2h3v2H7v-2h3v-2H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 2v9h16V6H4z"/></svg>';

// Put the SVG folder icon on the toolbar button too (emoji renders inconsistently).
filesBtn.innerHTML = FOLDER_ICON + "Files";
devicesBtn.innerHTML = MONITOR_ICON + "Devices";

const SPK_ON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style="vertical-align:-3px"><path d="M3 9v6h4l5 5V4L7 9H3zm11 -.03v8.06A4.5 4.5 0 0 0 16.5 12 4.5 4.5 0 0 0 14 8.97z"/></svg>';
const SPK_OFF =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style="vertical-align:-3px"><path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="M15 9.5l5 5M20 9.5l-5 5" stroke="currentColor" stroke-width="2"/></svg>';
muteBtn.innerHTML = SPK_ON;

function renderDir(path: string, entries: RemoteEntry[]) {
  currentPath = path;
  fpath.textContent = path === ROOT ? "This PC / root" : path;
  flist.innerHTML = "";
  const dirs = entries.filter((e) => e.isDir).sort((a, b) => a.name.localeCompare(b.name));
  const files = entries.filter((e) => !e.isDir).sort((a, b) => a.name.localeCompare(b.name));
  for (const e of [...dirs, ...files]) {
    const li = document.createElement("li");
    li.className = e.isDir ? "dir" : "file";
    const size = e.isDir ? "" : humanSize(e.size);
    li.innerHTML = `<span class="fname">${e.isDir ? FOLDER_ICON : FILE_ICON}${escapeHtml(e.name)}</span><span class="fsize">${size}</span>`;
    li.onclick = () => {
      if (e.isDir) fileConn?.listDir(joinPath(path, e.name));
      else {
        fnoteMsg(`Downloading ${e.name}…`);
        fileConn?.download(joinPath(path, e.name));
      }
    };
    flist.appendChild(li);
  }
}

function humanSize(n: number): string {
  if (n < 1024) return n + " B";
  const u = ["KB", "MB", "GB", "TB"];
  let i = -1;
  do {
    n /= 1024;
    i++;
  } while (n >= 1024 && i < u.length - 1);
  return n.toFixed(1) + " " + u[i];
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}

function openFileSession() {
  if (fileConn || !activeConfig) return;
  fileReady = false;
  fnoteMsg("Opening file session…");
  fileConn = new Connection(
    document.createElement("canvas"), // no video in file mode
    {
      onError: (t, m) => fnoteMsg(`${t}: ${m}`, "#f88"),
      onPasswordRequired: (wrong) => {
        if (!wrong) fileConn?.login(lastPassword);
        else fnoteMsg("File session: wrong password. Reconnect and re-enter it.", "#f88");
      },
      onPeerInfo: () => {
        fileReady = true;
        fnoteMsg("");
        fileConn?.listDir(currentPath || ROOT);
      },
      onFileDir: (path, entries) => renderDir(path, entries),
      onDownload: (name, blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        fnoteMsg(`Downloaded ${name}`, "#8f8");
      },
      onFileError: (m) => fnoteMsg("Error: " + m, "#f88"),
      onFileNote: (m) => fnoteMsg(m, "#8f8"),
    },
    activeConfig,
    { fileMode: true }
  );
  fileConn.start(connectedId);
}

$("filesBtn").onclick = () => {
  if (!conn) return;
  filesPanel.hidden = !filesPanel.hidden;
  if (filesPanel.hidden) return;
  if (!fileConn) openFileSession();
  else if (fileReady) fileConn.listDir(currentPath || ROOT);
};
$("fup").onclick = () => fileConn?.listDir(parentPath(currentPath));
$("frefresh").onclick = () => fileConn?.listDir(currentPath);
$("fclose").onclick = () => (filesPanel.hidden = true);
fuploadInput.onchange = () => {
  if (!fileConn || !fileReady || !fuploadInput.files) return;
  const files = Array.from(fuploadInput.files);
  fnoteMsg(`Uploading ${files.length} file(s) to ${currentPath}…`);
  for (const f of files) fileConn.upload(currentPath, f);
  fuploadInput.value = "";
};

let audioMuted = false;
muteBtn.onclick = () => {
  audioMuted = !audioMuted;
  conn?.setAudioMuted(audioMuted);
  muteBtn.innerHTML = audioMuted ? SPK_OFF : SPK_ON;
  muteBtn.title = audioMuted ? "Unmute remote audio" : "Mute remote audio";
};

function showOverlay(text: string | null) {
  if (text == null) {
    overlay.hidden = true;
  } else {
    overlay.hidden = false;
    overlayText.textContent = text;
  }
}

function populateDisplays(displays: hbb.IDisplayInfo[], current: number) {
  displaySelect.innerHTML = "";
  displays.forEach((d, i) => {
    const opt = document.createElement("option");
    opt.value = String(i);
    const name = d.name || `Display ${i + 1}`;
    opt.textContent = `${name} (${d.width ?? 0}×${d.height ?? 0})`;
    displaySelect.appendChild(opt);
  });
  displaySelect.value = String(current);
  displaySelect.hidden = displays.length <= 1; // only show when multi-monitor
}

displaySelect.onchange = () => {
  if (!conn) return;
  conn.switchDisplay(parseInt(displaySelect.value, 10));
  showOverlay("Switching display…");
  canvas.focus();
};

function askPassword(wrong: boolean) {
  pwTitle.textContent = wrong ? "Wrong password — try again" : "Password required";
  pwInput.value = "";
  pwmodal.hidden = false;
  pwInput.focus();
}

$("pwok").onclick = () => {
  pwmodal.hidden = true;
  lastPassword = pwInput.value; // reuse for the file-transfer session
  conn?.login(pwInput.value);
};
$("pwcancel").onclick = () => {
  pwmodal.hidden = true;
  disconnect();
};
pwInput.addEventListener("keydown", (e) => {
  if (e.key === "Enter") $<HTMLButtonElement>("pwok").click();
});

async function connect() {
  const form = currentForm();
  const id = peerId.value.trim().replace(/\s/g, "");
  if (!form.server) {
    settings.classList.add("open");
    showOverlay("Enter your ID/Rendezvous server first (⚙ Server).");
    idServerEl.focus();
    return;
  }
  if (!id) {
    peerId.focus();
    return;
  }
  if (!VideoRenderer.isSupported()) {
    showOverlay("This browser has no WebCodecs VideoDecoder — use a recent Chrome/Edge.");
    return;
  }
  saveForm(form, id); // persist to localStorage + URL hash
  settings.classList.remove("open");
  showOverlay("Connecting…");

  activeConfig = buildConfig(form);
  connectedId = id;

  // Tear down any prior session first — a lingering connection to the host can
  // make it refuse/skip encryption on the new one.
  conn?.close();
  conn = undefined;
  fileConn?.close();
  fileConn = undefined;
  fileReady = false;
  currentPath = ROOT;

  conn = new Connection(
    canvas,
    {
      onStatus: (s, d) => {
        setStatus(s, d);
        if (s === "rendezvous") showOverlay("Contacting ID server…");
        else if (s === "relay") showOverlay("Connecting to relay…");
        else if (s === "handshake") showOverlay("Securing connection…");
        else if (s === "logging-in") showOverlay("Logging in… waiting for image");
      },
      onError: (title, text) => {
        showOverlay(`${title}: ${text}`);
        setStatus("error", text);
      },
      onSecure: (secure) => console.log(secure ? "secured ✅" : "UNENCRYPTED ⚠️"),
      onPeerInfo: (pi) => {
        console.log("peer_info", pi);
        populateDisplays(pi.displays ?? [], pi.current_display ?? 0);
      },
      onDisplaySwitched: (i) => {
        displaySelect.value = String(i);
      },
      onPasswordRequired: (wrong) => {
        // Auto-login with the password handed off by the dashboard. If it's
        // wrong, drop it and fall back to the manual prompt.
        if (injectedPassword != null && !wrong) {
          lastPassword = injectedPassword;
          conn?.login(injectedPassword);
        } else {
          injectedPassword = null;
          askPassword(wrong);
        }
      },
      onFirstFrame: () => {
        showOverlay(null);
        applyQualityFps(); // push the user's saved quality/FPS to the host
      },
      onCursor: applyCursor,
      onCursorPosition: onHostCursorPosition,
      onClipboard: (text) => {
        navigator.clipboard?.writeText(text).catch((e) => console.warn("[clipboard] writeText", e));
      },
    },
    activeConfig!
  );
  await conn.start(id);
}

function disconnect() {
  conn?.close();
  conn = undefined;
  fileConn?.close();
  fileConn = undefined;
  fileReady = false;
  displaySelect.hidden = true;
  displaySelect.innerHTML = "";
  filesPanel.hidden = true;
  currentPath = ROOT;
  hostCursor = null;
  hostCursorEl.hidden = true;
  canvas.style.cursor = "default";
  showOverlay("Disconnected");
}

// Manual connects (button / Enter) never reuse a dashboard-supplied password.
connectBtn.onclick = () => {
  injectedPassword = null;
  connect();
};
disconnectBtn.onclick = disconnect;
peerId.addEventListener("keydown", (e) => {
  if (e.key === "Enter") {
    injectedPassword = null;
    connect();
  }
});

// Auto-connect when launched from the dashboard with credentials (see handoff.ts).
function connectFromDashboard(p: HandoffPayload) {
  if (p.server) {
    idServerEl.value = p.server;
    if (p.relay !== undefined) relayServerEl.value = p.relay;
    if (p.key !== undefined) keyEl.value = p.key;
    if (p.schema) schemaEl.value = p.schema;
  }
  peerId.value = p.id;
  injectedPassword = p.password ?? null;
  connect();
}

// ---- in-app device portal ---------------------------------------------------
// Fetches the device list from the same-origin dashboard API (session cookie)
// and connects on click. See portal.ts.
let portalDevices: PortalDevice[] = [];

function renderPortal(filter = "") {
  const q = filter.trim().toLowerCase();
  const items = portalDevices.filter(
    (d) =>
      !q ||
      d.id.toLowerCase().includes(q) ||
      (d.name && d.name.toLowerCase().includes(q)) ||
      (d.group && d.group.toLowerCase().includes(q))
  );
  plist.innerHTML = "";
  if (!items.length) {
    pnote.textContent = portalDevices.length ? "No matches." : "No devices.";
    return;
  }
  pnote.textContent = "";
  // Group devices under optional section headers.
  const groups = new Map<string, PortalDevice[]>();
  for (const d of items) {
    const g = d.group ?? "";
    (groups.get(g) ?? groups.set(g, []).get(g)!).push(d);
  }
  for (const [g, ds] of groups) {
    if (g) {
      const h = document.createElement("li");
      h.className = "pgroup";
      h.textContent = g;
      plist.appendChild(h);
    }
    ds.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
    for (const d of ds) {
      const li = document.createElement("li");
      li.className = "pdev";
      const dot =
        d.online === undefined ? "" : `<span class="pdot ${d.online ? "on" : "off"}"></span>`;
      li.innerHTML =
        `<span class="pname">${MONITOR_ICON}${dot}${escapeHtml(d.name || d.id)}</span>` +
        `<span class="pid">${escapeHtml(d.id)}</span>`;
      li.title = d.password ? "Connect (auto-login)" : "Connect (password prompt)";
      li.onclick = () => {
        portalPanel.hidden = true;
        connectFromDashboard(d);
        canvas.focus();
      };
      plist.appendChild(li);
    }
  }
}

async function loadPortal() {
  pnote.textContent = "Loading devices…";
  plist.innerHTML = "";
  try {
    portalDevices = await fetchDevices();
    renderPortal(psearch.value);
  } catch (e) {
    plist.innerHTML = "";
    if (e instanceof PortalAuthError) {
      const url = portalLoginUrl();
      pnote.innerHTML = url
        ? `Not signed in. <a href="${escapeHtml(url)}">Sign in to the dashboard</a>, then reopen this page.`
        : "Not signed in to the dashboard. Log in there, then reload.";
    } else {
      pnote.textContent = "Couldn't load devices: " + (e as Error).message;
    }
  }
}

devicesBtn.onclick = () => {
  portalPanel.hidden = !portalPanel.hidden;
  if (!portalPanel.hidden) {
    if (!portalDevices.length) loadPortal();
    psearch.focus();
  }
};
psearch.oninput = () => renderPortal(psearch.value);
$("prefresh").onclick = () => loadPortal();
$("pclose").onclick = () => (portalPanel.hidden = true);

// ---- remote cursor overlay --------------------------------------------------
// We hide the native cursor over the video and draw the HOST's cursor (its shape
// + position) as an overlay. It follows the local mouse instantly, and also
// reflects host-initiated cursor moves via cursor_position when the mouse is idle.
const srcCursor = document.createElement("canvas");
let hostCursor: { url: string; hotx: number; hoty: number; w: number; h: number } | null = null;
let lastLocalMove = 0;
let lastPointer = { x: 0, y: 0 }; // stage-relative hotspot point

function scaleOffsets() {
  const rect = canvas.getBoundingClientRect();
  const cw = canvas.width || 1;
  const ch = canvas.height || 1;
  const scale = Math.min(rect.width / cw, rect.height / ch) || 1;
  return { scale, offX: (rect.width - cw * scale) / 2, offY: (rect.height - ch * scale) / 2 };
}

// Place the cursor overlay so its hotspot sits at stage-relative (px, py).
function placeHostCursor(px: number, py: number) {
  lastPointer = { x: px, y: py };
  if (!hostCursor || hostCursorEl.hidden) return;
  const { scale } = scaleOffsets();
  hostCursorEl.style.width = hostCursor.w * scale + "px";
  hostCursorEl.style.height = hostCursor.h * scale + "px";
  hostCursorEl.style.left = px - hostCursor.hotx * scale + "px";
  hostCursorEl.style.top = py - hostCursor.hoty * scale + "px";
}

function applyCursor(c: CursorImage | null) {
  const expected = c ? c.width * c.height * 4 : 0;
  if (!c || !c.width || !c.height || c.rgba.length < expected) {
    hostCursor = null;
    hostCursorEl.hidden = true;
    canvas.style.cursor = "default"; // fall back to native arrow
    return;
  }
  srcCursor.width = c.width;
  srcCursor.height = c.height;
  const sctx = srcCursor.getContext("2d")!;
  sctx.clearRect(0, 0, c.width, c.height);
  sctx.putImageData(
    new ImageData(new Uint8ClampedArray(c.rgba.subarray(0, expected)), c.width, c.height),
    0,
    0
  );
  hostCursor = { url: srcCursor.toDataURL(), hotx: c.hotx, hoty: c.hoty, w: c.width, h: c.height };
  hostCursorEl.style.backgroundImage = `url(${hostCursor.url})`;
  hostCursorEl.hidden = false;
  canvas.style.cursor = "none"; // hide native; overlay represents the cursor
  placeHostCursor(lastPointer.x, lastPointer.y);
}

function onHostCursorPosition(rx: number, ry: number) {
  // Ignore the echo of our own move; apply host-initiated moves when idle.
  if (Date.now() - lastLocalMove < 250) return;
  const { scale, offX, offY } = scaleOffsets();
  placeHostCursor(offX + rx * scale, offY + ry * scale);
}

window.addEventListener("resize", () => placeHostCursor(lastPointer.x, lastPointer.y));

// ---- input capture ----------------------------------------------------------

function remoteXY(e: MouseEvent): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  const cw = canvas.width || 1;
  const ch = canvas.height || 1;
  const scale = Math.min(r.width / cw, r.height / ch) || 1;
  const drawnW = cw * scale;
  const drawnH = ch * scale;
  const offX = (r.width - drawnW) / 2;
  const offY = (r.height - drawnH) / 2;
  const x = (e.clientX - r.left - offX) / scale;
  const y = (e.clientY - r.top - offY) / scale;
  return { x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) };
}

const BTN = { left: 1 << 3, right: 2 << 3, mid: 4 << 3 };
function btnBits(button: number): number {
  if (button === 0) return BTN.left;
  if (button === 2) return BTN.right;
  if (button === 1) return BTN.mid;
  return 0;
}

let mouseDown = false;
let lastNudge = 0;
// Force the HOST to redraw its cursor. Absolute moves (SetCursorPos-style) don't
// generate WM_MOUSEMOVE over the bare desktop, so Windows — and VM guests
// especially — keep the cursor hidden. A net-zero relative wiggle produces real
// movement events; we re-assert the absolute position after to cancel drift.
function nudgeCursorVisible(x: number, y: number, e: MouseEvent) {
  const now = Date.now();
  if (!conn || mouseDown || now - lastNudge < 120) return;
  lastNudge = now;
  conn.inputMouseRelative(1, 0);
  conn.inputMouseRelative(-1, 0);
  conn.inputMouse(0, x, y, e.altKey, e.ctrlKey, e.shiftKey, e.metaKey);
}

canvas.addEventListener("mousemove", (e) => {
  if (!conn) return;
  const r = canvas.getBoundingClientRect();
  lastLocalMove = Date.now();
  placeHostCursor(e.clientX - r.left, e.clientY - r.top);
  const { x, y } = remoteXY(e);
  conn.inputMouse(0, x, y, e.altKey, e.ctrlKey, e.shiftKey, e.metaKey);
  nudgeCursorVisible(x, y, e);
});
canvas.addEventListener("mouseenter", () => {
  if (hostCursor) {
    hostCursorEl.hidden = false;
    placeHostCursor(lastPointer.x, lastPointer.y);
  }
});
canvas.addEventListener("mouseleave", () => {
  hostCursorEl.hidden = true; // don't leave a stray cursor over the video
});
canvas.addEventListener("mousedown", (e) => {
  if (!conn) return;
  e.preventDefault();
  canvas.focus();
  mouseDown = true;
  conn.resumeAudio(); // unlock/resume audio on user gesture
  const { x, y } = remoteXY(e);
  conn.inputMouse(1 | btnBits(e.button), x, y, e.altKey, e.ctrlKey, e.shiftKey, e.metaKey);
});
canvas.addEventListener("mouseup", (e) => {
  if (!conn) return;
  e.preventDefault();
  mouseDown = false;
  const { x, y } = remoteXY(e);
  conn.inputMouse(2 | btnBits(e.button), x, y, e.altKey, e.ctrlKey, e.shiftKey, e.metaKey);
});
canvas.addEventListener("contextmenu", (e) => e.preventDefault());
canvas.addEventListener(
  "wheel",
  (e) => {
    if (!conn) return;
    e.preventDefault();
    const y = Math.max(-3, Math.min(3, Math.round(-e.deltaY / 40) || (e.deltaY > 0 ? -1 : 1)));
    const x = Math.max(-3, Math.min(3, Math.round(-e.deltaX / 40) || 0));
    conn.inputMouse(3 | BTN.mid, x, y);
  },
  { passive: false }
);

const CTRL_KEYS: Record<string, hbb.ControlKey> = {
  Enter: hbb.ControlKey.Return,
  Backspace: hbb.ControlKey.Backspace,
  Tab: hbb.ControlKey.Tab,
  Escape: hbb.ControlKey.Escape,
  Delete: hbb.ControlKey.Delete,
  Home: hbb.ControlKey.Home,
  End: hbb.ControlKey.End,
  PageUp: hbb.ControlKey.PageUp,
  PageDown: hbb.ControlKey.PageDown,
  ArrowLeft: hbb.ControlKey.LeftArrow,
  ArrowRight: hbb.ControlKey.RightArrow,
  ArrowUp: hbb.ControlKey.UpArrow,
  ArrowDown: hbb.ControlKey.DownArrow,
};

canvas.addEventListener("keydown", (e) => {
  if (!conn) return;
  const combo = e.ctrlKey || e.metaKey || e.altKey;
  // Let the browser 'paste' event handle Ctrl/Cmd+V so we can push the clipboard
  // to the host *before* the paste keystroke lands.
  if (combo && (e.key === "v" || e.key === "V")) return;

  const ck = CTRL_KEYS[e.key];
  if (ck !== undefined) {
    e.preventDefault();
    conn.inputControlKey(ck, true, true, e.altKey, e.ctrlKey, e.shiftKey, e.metaKey);
  } else if (e.key.length === 1) {
    e.preventDefault();
    if (combo) {
      // Shortcut (Ctrl+C, Ctrl+A, …): send base char code + modifiers.
      conn.inputKeyChr(
        e.key.toLowerCase().charCodeAt(0),
        true,
        true,
        e.altKey,
        e.ctrlKey,
        e.shiftKey,
        e.metaKey
      );
    } else {
      conn.inputString(e.key);
    }
  }
});

// Paste (local -> remote): push local text to the host, then send Ctrl+V so the
// focused remote app pastes it. Small delay lets the host apply the clipboard
// before the keystroke. (Host is Windows -> Ctrl, regardless of Cmd on a Mac.)
document.addEventListener("paste", (e) => {
  // Only hijack paste when the remote screen is focused. Otherwise let the paste
  // go to whatever field has focus (password box, server settings, ID input).
  if (!conn || document.activeElement !== canvas) return;
  const text = e.clipboardData?.getData("text/plain") ?? "";
  e.preventDefault();
  if (!text) return;
  conn.sendClipboardText(text);
  setTimeout(() => conn?.inputKeyChr("v".charCodeAt(0), true, true, false, true, false, false), 60);
});

initCrypto().then(() => {
  setStatus("idle");
  console.log("crypto ready");
  // If a dashboard opened this window, receive credentials and auto-connect.
  initHandoff(connectFromDashboard);
  // If a same-origin device portal is configured, make it the landing view.
  if (isPortalEnabled()) {
    devicesBtn.hidden = false;
    settings.classList.remove("open"); // server comes from config/portal, not the user
    overlayText.textContent = "Loading your devices…";
    portalPanel.hidden = false;
    loadPortal();
  }
});
